import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  List,
  ListFormData,
  ListItem,
  ListStats,
  ListFilters,
  ListSort,
  BulkOperation,
  ItemFilters,
  ItemSort,
  ItemBulkOperation,
  UndoAction,
  UndoStack,
  DateScopedListData
} from '../types/lists';
import { listsService } from '../services/listsService';
import { personaService } from '../services/personaService';

interface ListsStoreState {
  // State
  lists: List[];
  filteredLists: List[];
  isLoading: boolean;
  error: string | null;
  completionVersion: number;
  
  // Filters and sorting
  filters: ListFilters;
  sort: ListSort;
  
  // Selected items for bulk operations
  selectedListIds: string[];
  
  // Actions
  initializeLists: () => void;
  
  // List CRUD
  createList: (data: ListFormData) => Promise<void>;
  updateList: (id: string, updates: Partial<List>) => Promise<void>;
  deleteList: (id: string) => Promise<void>;
  reorderLists: (reorderedLists: List[]) => Promise<void>;
  
  // List items
  addItem: (listId: string, content: string, itemData?: Partial<ListItem>) => Promise<void>;
  addItemsToList: (listId: string, itemsData: Array<{
    content: string;
    description?: string;
    estimatedMinutes?: number;
    difficulty?: 'easy' | 'medium' | 'hard';
    category?: string;
  }>) => Promise<ListItem[]>;
  updateItem: (listId: string, itemId: string, updates: Partial<ListItem>) => Promise<void>;
  deleteItem: (listId: string, itemId: string) => Promise<void>;
  reorderItems: (listId: string, itemId: string, newPriority: number) => Promise<void>;
  reorderItemsArray: (listId: string, reorderedItems: ListItem[]) => Promise<void>;
  
  // Universal Completion System
  updateItemCompletion: (listId: string, itemId: string, completed: boolean, contextDate?: Date) => Promise<void>;
  getListDataForContext: (listId: string, contextDate?: Date) => DateScopedListData | null;
  getCurrentListData: (listId: string) => DateScopedListData | null;
  
  // Enhanced item management
  searchItems: (listId: string, query: string) => ListItem[];
  getFilteredItems: (listId: string, filters: ItemFilters, sort?: ItemSort) => ListItem[];
  performItemBulkOperation: (listId: string, operation: ItemBulkOperation) => Promise<void>;
  
  // Item selection for bulk operations
  selectedItemIds: string[];
  setSelectedItems: (itemIds: string[]) => void;
  toggleItemSelection: (itemId: string) => void;
  selectAllItems: (listId: string) => void;
  clearItemSelection: () => void;
  
  // Undo functionality
  undoStack: UndoStack;
  performUndo: () => Promise<void>;
  canUndo: () => boolean;
  getLastUndoAction: () => UndoAction | null;
  clearUndoStack: () => void;
  
  // Clone template (creates a list from a template)
  cloneTemplate: (templateId: string, customizations?: Partial<ListFormData>) => Promise<void>;

  // Filtering and sorting
  setFilters: (filters: Partial<ListFilters>) => void;
  setSort: (sort: ListSort) => void;
  clearFilters: () => void;
  applyFiltersAndSort: () => void;
  
  // Bulk operations
  setSelectedLists: (listIds: string[]) => void;
  toggleListSelection: (listId: string) => void;
  selectAllLists: () => void;
  clearSelection: () => void;
  performBulkOperation: (operation: BulkOperation) => Promise<void>;
  
  // Utility methods
  getListById: (id: string) => List | undefined;
  getListsByStatus: (status: string) => List[];
  getListsByGoalId: (goalId: string) => List[];
  getListStats: () => ListStats;

  // Error handling
  clearError: () => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
  
  // Helper methods (internal)
  addToUndoStack: (action: UndoAction) => void;
  getReverseOperation: (operation: ItemBulkOperation) => ItemBulkOperation | null;
}

const defaultFilters: ListFilters = {
  status: undefined,
  goalId: undefined,
  search: undefined,
  hasDeadline: undefined,
  isOverdue: undefined,
  dateRange: undefined,
};

const defaultSort: ListSort = {
  field: 'priority',
  direction: 'asc',
};

export const useListsStore = create<ListsStoreState>()(
  devtools(
    (set, get) => ({
      // Initial state
      lists: [],
      filteredLists: [],
      isLoading: false,
      error: null,
      completionVersion: 0,
      filters: defaultFilters,
      sort: defaultSort,
      selectedListIds: [],
      
      // Enhanced item management state
      selectedItemIds: [],
      undoStack: {
        actions: [],
        maxSize: 10,
        currentIndex: -1
      },

      // Initialize lists
      initializeLists: () => {
        set({ isLoading: true, error: null });
        try {
          // Ensure persona data is loaded
          personaService.initializeIfNeeded();

          listsService.initializeMockData();
          const lists = listsService.getAllLists();

          console.log('📋 Lists Store initialized:', {
            listsCount: lists.length,
            listIds: lists.map(l => l.id),
            listTitles: lists.map(l => `${l.id}: ${l.title}`)
          });

          set({
            lists,
            filteredLists: lists,
            isLoading: false
          });

          // Apply current filters and sort
          get().applyFiltersAndSort();
        } catch (error) {
          console.error('Failed to initialize lists:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to initialize lists',
            isLoading: false
          });
        }
      },

      // Create new list
      createList: async (data: ListFormData) => {
        set({ isLoading: true, error: null });
        try {
          const newList = await listsService.createList(data);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists, isLoading: false });
          get().applyFiltersAndSort();
          console.log('✅ List created successfully:', newList.title);
        } catch (error) {
          console.error('Failed to create list:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to create list',
            isLoading: false 
          });
          throw error;
        }
      },

      // Update existing list
      updateList: async (id: string, updates: Partial<List>) => {
        set({ error: null });
        try {
          const updatedList = await listsService.updateList(id, updates);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists });
          get().applyFiltersAndSort();
          console.log('✅ List updated successfully:', updatedList.title);
        } catch (error) {
          console.error('Failed to update list:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to update list' });
          throw error;
        }
      },

      // Delete list
      deleteList: async (id: string) => {
        set({ error: null });
        try {
          await listsService.deleteList(id);
          const updatedLists = listsService.getAllLists();
          set({ 
            lists: updatedLists,
            selectedListIds: get().selectedListIds.filter(selectedId => selectedId !== id)
          });
          get().applyFiltersAndSort();
          console.log('✅ List deleted successfully');
        } catch (error) {
          console.error('Failed to delete list:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to delete list' });
          throw error;
        }
      },

      // Reorder lists (drag and drop)
      reorderLists: async (reorderedLists: List[]) => {
        try {
          await listsService.reorderLists(reorderedLists);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists });
          get().applyFiltersAndSort();
          console.log('✅ Lists reordered successfully');
        } catch (error) {
          console.error('Failed to reorder lists:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to reorder lists' });
          throw error;
        }
      },

      // Add item to list
      addItem: async (listId: string, content: string, itemData?: Partial<ListItem>) => {
        try {
          await listsService.addItem(listId, content, itemData);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists });
          get().applyFiltersAndSort();
          console.log('✅ Item added successfully');
        } catch (error) {
          console.error('Failed to add item:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to add item' });
          throw error;
        }
      },

      // Add multiple items to a list
      addItemsToList: async (listId: string, itemsData: Array<{
        content: string;
        description?: string;
        estimatedMinutes?: number;
        difficulty?: 'easy' | 'medium' | 'hard';
        category?: string;
      }>) => {
        try {
          const list = get().lists.find(l => l.id === listId);
          if (!list) {
            throw new Error('List not found');
          }

          // Find highest priority to start from
          const highestPriority = Math.max(0, ...list.items.map(item => item.priority));
          
          // Create new items with sequential priorities
          const newItems: ListItem[] = itemsData.map((itemData, index) => ({
            id: crypto.randomUUID(),
            content: itemData.content,
            description: itemData.description,
            estimatedMinutes: itemData.estimatedMinutes,
            difficulty: itemData.difficulty,
            category: itemData.category,
            priority: highestPriority + index + 1,
            createdAt: new Date(),
            updatedAt: new Date()
          }));

          // Update the list with new items
          const updatedList = {
            ...list,
            items: [...list.items, ...newItems],
            updatedAt: new Date()
          };

          // Save to service
          await listsService.updateList(listId, updatedList);
          
          // Update store
          set(state => ({
            lists: state.lists.map(l => 
              l.id === listId ? updatedList : l
            )
          }));

          get().applyFiltersAndSort();
          console.log(`✅ Added ${newItems.length} items to list "${list.title}"`);
          return newItems;
        } catch (error) {
          console.error('Failed to add items to list:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to add items to list' });
          throw error;
        }
      },

      // Update item
      updateItem: async (listId: string, itemId: string, updates: Partial<ListItem>) => {
        try {
          await listsService.updateItem(listId, itemId, updates);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists });
          get().applyFiltersAndSort();
          
          // REMOVED: completion tracking moved to Universal Completion Service
          console.log('🎯 Item updated!');
        } catch (error) {
          console.error('Failed to update item:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to update item' });
          throw error;
        }
      },

      // Delete item
      deleteItem: async (listId: string, itemId: string) => {
        try {
          await listsService.deleteItem(listId, itemId);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists });
          get().applyFiltersAndSort();
          console.log('✅ Item deleted successfully');
        } catch (error) {
          console.error('Failed to delete item:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to delete item' });
          throw error;
        }
      },

      // Reorder items within a list
      reorderItems: async (listId: string, itemId: string, newPriority: number) => {
        try {
          await listsService.reorderItems(listId, itemId, newPriority);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists });
          get().applyFiltersAndSort();
          console.log('✅ Items reordered successfully');
        } catch (error) {
          console.error('Failed to reorder items:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to reorder items' });
          throw error;
        }
      },

      reorderItemsArray: async (listId: string, reorderedItems: ListItem[]) => {
        try {
          await listsService.reorderItemsArray(listId, reorderedItems);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists });
          get().applyFiltersAndSort();
          console.log('✅ Items array reordered successfully');
        } catch (error) {
          console.error('Failed to reorder items array:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to reorder items array' });
          throw error;
        }
      },

      // Universal Completion System methods
      updateItemCompletion: async (listId: string, itemId: string, completed: boolean, contextDate?: Date) => {
        try {
          await listsService.updateItemCompletion(listId, itemId, completed, contextDate);
          // Increment completionVersion to trigger re-renders in components like WeeklyCalendarView
          set(state => ({ completionVersion: state.completionVersion + 1 }));
          console.log(`✅ Item completion updated: ${completed ? 'completed' : 'incomplete'}`);
        } catch (error) {
          console.error('Failed to update item completion:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to update item completion' });
          throw error;
        }
      },

      getListDataForContext: (listId: string, contextDate?: Date) => {
        return listsService.getListDataForContext(listId, contextDate);
      },

      getCurrentListData: (listId: string) => {
        return listsService.getCurrentListData(listId);
      },

      // Search items within a list
      searchItems: (listId: string, query: string) => {
        return listsService.searchItems(listId, query);
      },

      // Get filtered and sorted items
      getFilteredItems: (listId: string, filters: ItemFilters, sort?: ItemSort, contextDate?: Date) => {
        return listsService.getFilteredItems(listId, filters, sort, contextDate);
      },

      // Perform bulk operations on items
      performItemBulkOperation: async (listId: string, operation: ItemBulkOperation) => {
        try {
          // Add to undo stack before performing operation
          if (operation.type !== 'delete') {
            const list = get().getListById(listId);
            if (list) {
              const affectedItems = list.items.filter(item => operation.itemIds.includes(item.id));
              const undoData = {
                listId,
                operation: operation,
                items: affectedItems.map(item => ({ ...item })) // Deep copy
              };

              get().addToUndoStack({
                id: `undo_${Date.now()}`,
                type: 'bulk_operation',
                timestamp: new Date(),
                listId,
                description: `Bulk ${operation.type} on ${operation.itemIds.length} items`,
                data: undoData,
                canUndo: true
              });
            }
          }

          await listsService.performItemBulkOperation(listId, operation);
          const updatedLists = listsService.getAllLists();
          set({ lists: updatedLists });
          get().applyFiltersAndSort();
          console.log(`✅ Bulk operation ${operation.type} completed successfully`);
        } catch (error) {
          console.error('Failed to perform bulk item operation:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to perform bulk operation' });
          throw error;
        }
      },

      // Item selection management (fix duplicate property)
      // selectedItemIds already declared in interface
      setSelectedItems: (itemIds: string[]) => {
        set({ selectedItemIds: itemIds });
      },

      toggleItemSelection: (itemId: string) => {
        const { selectedItemIds } = get();
        const newSelection = selectedItemIds.includes(itemId)
          ? selectedItemIds.filter(id => id !== itemId)
          : [...selectedItemIds, itemId];
        set({ selectedItemIds: newSelection });
      },

      selectAllItems: (listId: string) => {
        const list = get().getListById(listId);
        if (list) {
          set({ selectedItemIds: list.items.map(item => item.id) });
        }
      },

      clearItemSelection: () => {
        set({ selectedItemIds: [] });
      },

      // Undo functionality
      performUndo: async () => {
        const { undoStack } = get();
        if (undoStack.currentIndex >= 0) {
          const action = undoStack.actions[undoStack.currentIndex];
          
          try {
            switch (action.type) {
              case 'item_complete':
                // REMOVED: completion handled by Universal Completion Service
                await get().updateItemCompletion(action.listId, action.data.itemId, false);
                break;
              case 'item_delete':
                // Re-add the deleted item
                await get().addItem(action.listId, action.data.content, action.data);
                break;
              case 'bulk_operation': {
                // Reverse the bulk operation
                const reverseOperation = get().getReverseOperation(action.data.operation);
                if (reverseOperation) {
                  await listsService.performItemBulkOperation(action.listId, reverseOperation);
                }
                break;
              }
            }

            // Move undo stack pointer
            set({
              undoStack: {
                ...undoStack,
                currentIndex: undoStack.currentIndex - 1
              }
            });

            console.log(`✅ Undid: ${action.description}`);
          } catch (error) {
            console.error('Failed to undo action:', error);
            set({ error: error instanceof Error ? error.message : 'Failed to undo action' });
          }
        }
      },

      canUndo: () => {
        const { undoStack } = get();
        return undoStack.currentIndex >= 0;
      },

      getLastUndoAction: () => {
        const { undoStack } = get();
        return undoStack.currentIndex >= 0 ? undoStack.actions[undoStack.currentIndex] : null;
      },

      clearUndoStack: () => {
        set({
          undoStack: {
            actions: [],
            maxSize: 10,
            currentIndex: -1
          }
        });
      },

      // Helper method to add actions to undo stack
      addToUndoStack: (action: UndoAction) => {
        const { undoStack } = get();
        const newActions = [...undoStack.actions];
        
        // Remove any actions after current index (if we undid and then did something new)
        if (undoStack.currentIndex < newActions.length - 1) {
          newActions.splice(undoStack.currentIndex + 1);
        }
        
        // Add new action
        newActions.push(action);
        
        // Limit stack size
        if (newActions.length > undoStack.maxSize) {
          newActions.shift();
        }
        
        set({
          undoStack: {
            ...undoStack,
            actions: newActions,
            currentIndex: newActions.length - 1
          }
        });
      },

      // Helper to get reverse operation for undo
      getReverseOperation: (operation: ItemBulkOperation) => {
        switch (operation.type) {
          case 'complete':
            return { ...operation, type: 'uncomplete' as const };
          case 'uncomplete':
            return { ...operation, type: 'complete' as const };
          default:
            return null;
        }
      },

      // Clone template to create new list (template CRUD is handled by templatesStore)
      cloneTemplate: async (templateId: string, customizations?: Partial<ListFormData>) => {
        set({ isLoading: true, error: null });
        try {
          const newList = await listsService.cloneTemplate(templateId, customizations);
          const updatedLists = listsService.getAllLists();
          set({
            lists: updatedLists,
            isLoading: false
          });
          get().applyFiltersAndSort();
          console.log('✅ Template cloned successfully:', newList.title);
        } catch (error) {
          console.error('Failed to clone template:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to clone template',
            isLoading: false
          });
          throw error;
        }
      },

      // Set filters
      setFilters: (newFilters: Partial<ListFilters>) => {
        const filters = { ...get().filters, ...newFilters };
        set({ filters });
        get().applyFiltersAndSort();
      },

      // Set sort
      setSort: (sort: ListSort) => {
        set({ sort });
        get().applyFiltersAndSort();
      },

      // Clear filters
      clearFilters: () => {
        set({ filters: defaultFilters });
        get().applyFiltersAndSort();
      },

      // Apply filters and sort
      applyFiltersAndSort: () => {
        const { lists, filters, sort } = get();
        let filtered = [...lists];

        // Apply filters
        if (filters.status && filters.status.length > 0) {
          filtered = filtered.filter(list => filters.status!.includes(list.status));
        }

        if (filters.goalId && filters.goalId.length > 0) {
          filtered = filtered.filter(list => 
            list.goalId && filters.goalId!.includes(list.goalId)
          );
        }

        // Aspiration filtering will be handled at the page level since it requires goal data

        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filtered = filtered.filter(list =>
            list.title.toLowerCase().includes(searchLower) ||
            list.description.toLowerCase().includes(searchLower)
          );
        }

        if (filters.hasDeadline !== undefined) {
          filtered = filtered.filter(list =>
            filters.hasDeadline ? list.deadline : !list.deadline
          );
        }

        if (filters.isOverdue) {
          const now = new Date();
          filtered = filtered.filter(list =>
            list.deadline && 
            list.deadline < now && 
            list.status !== 'complete'
          );
        }

        if (filters.dateRange) {
          filtered = filtered.filter(list => {
            const createdAt = new Date(list.createdAt);
            return createdAt >= filters.dateRange!.start && 
                   createdAt <= filters.dateRange!.end;
          });
        }

        // Apply sort
        filtered.sort((a, b) => {
          let aValue: any;
          let bValue: any;

          switch (sort.field) {
            case 'title':
              aValue = a.title.toLowerCase();
              bValue = b.title.toLowerCase();
              break;
            case 'priority':
              aValue = a.priority;
              bValue = b.priority;
              break;
            case 'progress': {
              // Use current context completion data for sorting
              const aData = get().getCurrentListData(a.id);
              const bData = get().getCurrentListData(b.id);
              aValue = aData ? aData.progress : 0;
              bValue = bData ? bData.progress : 0;
              break;
            }
            case 'deadline':
              aValue = a.deadline ? a.deadline.getTime() : 0;
              bValue = b.deadline ? b.deadline.getTime() : 0;
              break;
            case 'updatedAt':
              aValue = new Date(a.updatedAt).getTime();
              bValue = new Date(b.updatedAt).getTime();
              break;
            case 'createdAt':
              aValue = new Date(a.createdAt).getTime();
              bValue = new Date(b.createdAt).getTime();
              break;
            case 'status':
              aValue = a.status;
              bValue = b.status;
              break;
            default:
              aValue = a.priority;
              bValue = b.priority;
          }

          if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
          return 0;
        });

        set({ filteredLists: filtered });
      },

      // Bulk operation management
      setSelectedLists: (listIds: string[]) => {
        set({ selectedListIds: listIds });
      },

      toggleListSelection: (listId: string) => {
        const { selectedListIds } = get();
        const newSelection = selectedListIds.includes(listId)
          ? selectedListIds.filter(id => id !== listId)
          : [...selectedListIds, listId];
        set({ selectedListIds: newSelection });
      },

      selectAllLists: () => {
        const { filteredLists } = get();
        set({ selectedListIds: filteredLists.map(list => list.id) });
      },

      clearSelection: () => {
        set({ selectedListIds: [] });
      },

      performBulkOperation: async (operation: BulkOperation) => {
        set({ error: null });
        try {
          await listsService.performBulkOperation(operation);
          const updatedLists = listsService.getAllLists();
          set({ 
            lists: updatedLists,
            selectedListIds: operation.type === 'delete' ? [] : get().selectedListIds
          });
          get().applyFiltersAndSort();
          console.log(`✅ Bulk operation ${operation.type} completed successfully`);
        } catch (error) {
          console.error('Failed to perform bulk operation:', error);
          set({ error: error instanceof Error ? error.message : 'Failed to perform bulk operation' });
          throw error;
        }
      },

      // Utility methods
      getListById: (id: string) => {
        return get().lists.find(list => list.id === id);
      },

      getListsByStatus: (status: string) => {
        return get().lists.filter(list => list.status === status)
          .sort((a, b) => a.priority - b.priority);
      },

      getListsByGoalId: (goalId: string) => {
        return get().lists.filter(list => list.goalId === goalId)
          .sort((a, b) => a.priority - b.priority);
      },

      getListStats: () => {
        return listsService.getListStats();
      },

      // Error handling
      clearError: () => set({ error: null }),
      
      setError: (error: string) => set({ error }),

      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'lists-store'
    }
  )
);